import React, { useMemo } from 'react';

import { DateTime } from 'luxon';
import { useTranslation } from 'react-i18next';

import { SHORT_TIME } from 'lane-shared/helpers/constants/dates';
import { doDateRangesOverlap } from 'lane-shared/helpers/dates';
import { dateFormatter } from 'lane-shared/helpers/formatters';
import { DateRangeType } from 'lane-shared/types/baseTypes/DateRangeType';
import { ReservableAvailabilityRange } from 'lane-shared/hooks/useReservableAvailabilityByRange';
import {
  findOverlappingCapacity,
  getSlotCapacityForRange,
} from 'domains-reservable-availability';

import { ChipSubLabelVariant } from '../ChipButton';
import { getChipCapacityProps } from '../ChipButton/getChipCapacityProps';
import { hasSlotEnded } from '../ChipButton/hasSlotEnded';
import { hasSlotStartedToday } from '../ChipButton/hasSlotStartedToday';
import Chips from '../Chips';

import styles from './index.scss';

interface Props {
  options: DateRangeType[];
  onClick: (range: DateRangeType) => void;
  unavailableDateRanges?: DateRangeType[];
  value: DateRangeType;
  timeZone?: string;
  availability?: ReservableAvailabilityRange[];
  maxQuantityPerSlot?: number;
  hideInProgressSlots: boolean;
}

export function CustomTimeChips({
  options,
  onClick,
  unavailableDateRanges = [],
  value,
  timeZone,
  availability = [],
  maxQuantityPerSlot,
  hideInProgressSlots,
}: Props) {
  const { t } = useTranslation();
  const now = timeZone ? DateTime.now().setZone(timeZone) : DateTime.now();
  const isPastSlot = hideInProgressSlots ? hasSlotStartedToday : hasSlotEnded;

  const availableTimes = useMemo(
    () => {
      return options
        .filter(option => {
          if (maxQuantityPerSlot != null && isPastSlot(option, now)) return false;

          const isUnavailable = unavailableDateRanges.some(range =>
            doDateRangesOverlap(option, range)
          );

          if (!isUnavailable) return true;
          // When FF is on, re-include slots that the resolver marked as
          // Full/UserCapReached — they render as disabled chips instead of
          // disappearing from the list.

          if (maxQuantityPerSlot != null) {
            return findOverlappingCapacity(option, availability).length > 0;
          }

          return false;
        })
        .map(option => {
          let subLabel: string | undefined;
          let subLabelVariant: ChipSubLabelVariant = 'default';
          let disabled = false;

          if (maxQuantityPerSlot != null) {
            ({ subLabel, subLabelVariant, disabled } = getChipCapacityProps(
              getSlotCapacityForRange(option, availability, maxQuantityPerSlot),
              t
            ));
          }

          return {
            label: `${dateFormatter(
              option.startDate,
              SHORT_TIME,
              timeZone
            )} - ${dateFormatter(option.endDate, SHORT_TIME, timeZone)}`,
            value: option,
            subLabel,
            subLabelVariant,
            disabled,
          };
        });
    },
    [
      options,
      unavailableDateRanges,
      timeZone,
      availability,
      maxQuantityPerSlot,
      isPastSlot,
      t,
      now,
    ]
  );

  const onClickHandler = (selectedValue: DateRangeType) => {
    if (doDateRangesOverlap(selectedValue, value)) {
      onClick({ startDate: null, endDate: null });
    } else {
      onClick(selectedValue);
    }
  };

  if (availableTimes.length === 0) {
    return (
      <p className={styles.noAvailable}>
        {t('web.content.feature.reservable.customTimeSlots.noAvailability')}
      </p>
    );
  }

  return (
    <Chips<DateRangeType>
      onClick={onClickHandler}
      compare={range => doDateRangesOverlap(range, value)}
      options={availableTimes}
      itemName="time"
      gridSize="medium"
      fixedSize={maxQuantityPerSlot != null}
    />
  );
}
